import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { AdminUser } from '../lib/types.ts';

interface AuthCtx {
  user: AdminUser | null;
  token: string | null;
  isLoading: boolean;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthCtx | null>(null);

/**
 * Quién entró, según el token.
 *
 * ⚠️ `custom:roles` y `custom:tenantScope` **no los setea nadie**: los inyectaría
 * un Lambda pre-token que nunca se construyó (FOLLOWUP-18). Salen vacíos
 * siempre, y `tenantScope: []` significa *acceso a toda la plataforma* — o sea
 * que el panel cree que todos son super_admin. **La autorización real vive en
 * el backend**, que resuelve el `UserRecord` desde DynamoDB.
 *
 * `custom:tenantId` sí existe: lo setea el alta pública al crear el usuario. Es
 * lo único fiable del token para saber de qué negocio es esta cuenta.
 */
export function decodeJwt(token: string): AdminUser | null {
  try {
    const [, payload] = token.split('.');
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return {
      sub: decoded.sub as string,
      email: (decoded.email ?? decoded['cognito:username'] ?? '') as string,
      assignedRoles: (decoded['custom:roles'] as string[] | undefined) ?? [],
      tenantScope: (decoded['custom:tenantScope'] as string[] | undefined) ?? [],
      // El negocio de esta cuenta. `null` para un operador nuestro, que no
      // tiene uno propio — y ahí «ninguno» es la respuesta correcta, no «el
      // primero de la lista».
      tenantId: (decoded['custom:tenantId'] as string | undefined) ?? null,
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('admin_token'));
  const [user, setUser] = useState<AdminUser | null>(() => {
    const t = localStorage.getItem('admin_token');
    return t ? decodeJwt(t) : null;
  });
  const [isLoading, setIsLoading] = useState(false);

  // Re-hydrate on token change
  useEffect(() => {
    if (token) {
      setUser(decodeJwt(token));
    } else {
      setUser(null);
    }
  }, [token]);

  const login = useCallback((newToken: string) => {
    localStorage.setItem('admin_token', newToken);
    setToken(newToken);
    setIsLoading(false);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
